import JSZip from 'jszip';
import { frameExportNames, forEachFrameImage, sanitizeFilename } from './frameExport.js';
import { ImageDocument } from '../model/ImageDocument.js';
import { Layer } from '../model/Layer.js';
import { Palette } from '../model/Palette.js';
import { Renderer } from '../render/Renderer.js';
import { TRANSPARENT } from '../constants.js';

// ─── PIX8 Project Format ─────────────────────────────────────────────────

export function savePix8(doc) {
    const meta = {
        version: 1,
        width: doc.width,
        height: doc.height,
        palette: doc.palette.export(),
        layers: doc.layers.map(l => ({
            name: l.name,
            visible: l.visible,
            locked: l.locked,
            opacity: l.opacity,
            width: l.width,
            height: l.height,
            offsetX: l.offsetX,
            offsetY: l.offsetY,
            type: l.type || 'raster',
            textData: l.textData || null,
            isFixedSize: l.isFixedSize || false,
        })),
        activeLayerIndex: doc.activeLayerIndex,
        fgColorIndex: doc.fgColorIndex,
        bgColorIndex: doc.bgColorIndex,
        gridSize: doc.gridSize,
        animationEnabled: doc.animationEnabled,
        onionSkinning: doc.onionSkinning,
        onionOpacity: doc.onionOpacity,
        onionExtended: doc.onionExtended,
        // Frames reference cels by id; the cel table is written once, so cels
        // linked across several frames cost one copy on disk, not one per frame.
        frames: doc.animationEnabled ? doc.frames.map(f => ({
            tag: f.tag,
            delay: f.delay,
            celIds: f.celIds.slice(),
        })) : [],
        cels: doc.animationEnabled ? [...doc.cels].map(([id, cel]) => ({
            id,
            opacity: cel.opacity,
            textData: cel.textData,
            offsetX: cel.offsetX,
            offsetY: cel.offsetY,
            width: cel.width,
            height: cel.height,
        })) : [],
        nextCelId: doc._nextCelId,
        activeFrameIndex: doc.activeFrameIndex,
    };

    const metaJson = JSON.stringify(meta);
    const metaBytes = new TextEncoder().encode(metaJson);

    // Calculate total size: 4 bytes meta length + meta + base layer data + frame pixel data
    let totalLayerBytes = 0;
    for (const layer of doc.layers) {
        totalLayerBytes += layer.width * layer.height * 2;
    }

    let totalFrameBytes = 0;
    if (doc.animationEnabled) {
        for (const cel of doc.cels.values()) {
            totalFrameBytes += cel.data.length * 2;
        }
    }

    const totalSize = 4 + metaBytes.length + totalLayerBytes + totalFrameBytes;
    const buffer = new ArrayBuffer(totalSize);
    const view = new DataView(buffer);
    const bytes = new Uint8Array(buffer);

    view.setUint32(0, metaBytes.length, true);
    bytes.set(metaBytes, 4);

    let offset = 4 + metaBytes.length;
    for (const layer of doc.layers) {
        const u8View = new Uint8Array(layer.data.buffer, layer.data.byteOffset, layer.data.byteLength);
        bytes.set(u8View, offset);
        offset += layer.width * layer.height * 2;
    }

    // Write cel pixel data, in the same order as meta.cels
    if (doc.animationEnabled) {
        for (const cel of doc.cels.values()) {
            const u8View = new Uint8Array(cel.data.buffer, cel.data.byteOffset, cel.data.byteLength);
            bytes.set(u8View, offset);
            offset += cel.data.length * 2;
        }
    }

    return new Blob([buffer], { type: 'application/octet-stream' });
}

export function loadPix8(arrayBuffer) {
    const view = new DataView(arrayBuffer);
    const bytes = new Uint8Array(arrayBuffer);

    const metaLen = view.getUint32(0, true);
    const metaJson = new TextDecoder().decode(bytes.slice(4, 4 + metaLen));
    const meta = JSON.parse(metaJson);

    const doc = new ImageDocument(meta.width, meta.height);
    doc.palette.import(meta.palette);
    doc.fgColorIndex = meta.fgColorIndex;
    doc.bgColorIndex = meta.bgColorIndex;
    doc.gridSize = meta.gridSize ?? 16;

    // Remove default layer
    doc.layers = [];

    let offset = 4 + metaLen;
    for (const layerMeta of meta.layers) {
        // Per-layer dimensions (fall back to doc dimensions for old files)
        const lw = layerMeta.width ?? meta.width;
        const lh = layerMeta.height ?? meta.height;
        const layer = new Layer(layerMeta.name, lw, lh);
        layer.visible = layerMeta.visible;
        layer.locked = layerMeta.locked;
        layer.opacity = layerMeta.opacity ?? 1;
        layer.offsetX = layerMeta.offsetX ?? 0;
        layer.offsetY = layerMeta.offsetY ?? 0;
        layer.type = layerMeta.type || 'raster';
        layer.textData = layerMeta.textData || null;
        layer.isFixedSize = !!layerMeta.isFixedSize;
        const layerByteSize = lw * lh * 2;
        const u8View = new Uint8Array(layer.data.buffer, layer.data.byteOffset, layer.data.byteLength);
        u8View.set(bytes.slice(offset, offset + layerByteSize));
        offset += layerByteSize;
        doc.layers.push(layer);
    }

    doc.activeLayerIndex = meta.activeLayerIndex || 0;
    doc.selectedLayerIndices.add(doc.activeLayerIndex);

    // Load animation frames
    if (meta.animationEnabled && meta.frames && meta.frames.length > 0) {
        doc.animationEnabled = true;
        doc.onionSkinning = !!meta.onionSkinning;
        doc.onionOpacity = meta.onionOpacity ?? 50;
        doc.onionExtended = !!meta.onionExtended;
        doc.activeFrameIndex = meta.activeFrameIndex || 0;

        // Cel table, read in the same order it was written
        doc.cels = new Map();
        let maxCelId = 0;
        for (const celMeta of (meta.cels || [])) {
            const lw = celMeta.width ?? meta.width;
            const lh = celMeta.height ?? meta.height;
            const pixelCount = lw * lh;
            const data = new Uint16Array(pixelCount);
            const u8View = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
            u8View.set(bytes.slice(offset, offset + pixelCount * 2));
            offset += pixelCount * 2;
            doc.cels.set(celMeta.id, {
                data,
                opacity: celMeta.opacity ?? 1,
                textData: celMeta.textData || null,
                offsetX: celMeta.offsetX ?? 0,
                offsetY: celMeta.offsetY ?? 0,
                width: lw,
                height: lh,
            });
            if (celMeta.id > maxCelId) maxCelId = celMeta.id;
        }
        doc._nextCelId = meta.nextCelId ?? maxCelId + 1;

        doc.frames = meta.frames.map(frameMeta => ({
            tag: frameMeta.tag || '',
            delay: frameMeta.delay || 100,
            celIds: (frameMeta.celIds || []).slice(),
        }));
        // Load the active frame into layers
        if (doc.frames[doc.activeFrameIndex]) {
            doc._restoreLayersFromFrame(doc.frames[doc.activeFrameIndex]);
        }
    }

    return doc;
}

// ─── BMP (8-bit, 256 colors) ────────────────────────────────────────────

export function exportBMP(doc) {
    const flat = doc.flattenToLayer();
    const w = doc.width;
    const h = doc.height;

    // Row padding: rows must be multiple of 4 bytes
    const rowStride = Math.ceil(w / 4) * 4;
    const pixelDataSize = rowStride * h;
    const paletteSize = 256 * 4; // RGBX per entry
    const headerSize = 14 + 40; // BMP header + DIB header
    const fileSize = headerSize + paletteSize + pixelDataSize;

    const buffer = new ArrayBuffer(fileSize);
    const view = new DataView(buffer);
    const bytes = new Uint8Array(buffer);

    // BMP File Header (14 bytes)
    bytes[0] = 0x42; bytes[1] = 0x4D; // 'BM'
    view.setUint32(2, fileSize, true);
    view.setUint16(6, 0, true); // reserved
    view.setUint16(8, 0, true); // reserved
    view.setUint32(10, headerSize + paletteSize, true); // pixel data offset

    // DIB Header (BITMAPINFOHEADER, 40 bytes)
    view.setUint32(14, 40, true); // header size
    view.setInt32(18, w, true);
    view.setInt32(22, h, true); // positive = bottom-up
    view.setUint16(26, 1, true); // color planes
    view.setUint16(28, 8, true); // bits per pixel
    view.setUint32(30, 0, true); // compression (none)
    view.setUint32(34, pixelDataSize, true);
    view.setInt32(38, 2835, true); // h resolution (72 DPI)
    view.setInt32(42, 2835, true); // v resolution
    view.setUint32(46, 256, true); // colors used
    view.setUint32(50, 256, true); // important colors

    // Color table (256 entries, 4 bytes each: B, G, R, 0)
    let off = 54;
    for (let i = 0; i < 256; i++) {
        const [r, g, b] = doc.palette.getColor(i);
        bytes[off++] = b;
        bytes[off++] = g;
        bytes[off++] = r;
        bytes[off++] = 0;
    }

    // Pixel data (bottom-to-top); TRANSPARENT pixels become index 0
    const pixelOffset = headerSize + paletteSize;
    for (let y = 0; y < h; y++) {
        const srcRow = h - 1 - y; // BMP is bottom-up
        const dstRowStart = pixelOffset + y * rowStride;
        for (let x = 0; x < w; x++) {
            const v = flat.getPixel(x, srcRow);
            bytes[dstRowStart + x] = v === TRANSPARENT ? 0 : v;
        }
        // Padding bytes remain 0
    }

    return new Blob([buffer], { type: 'image/bmp' });
}

export function importBMP(arrayBuffer) {
    const view = new DataView(arrayBuffer);
    const bytes = new Uint8Array(arrayBuffer);

    // Validate BMP signature
    if (bytes[0] !== 0x42 || bytes[1] !== 0x4D) {
        throw new Error('Not a valid BMP file');
    }

    const pixelDataOffset = view.getUint32(10, true);
    const dibHeaderSize = view.getUint32(14, true);
    const w = view.getInt32(18, true);
    const h = Math.abs(view.getInt32(22, true));
    const topDown = view.getInt32(22, true) < 0;
    const bpp = view.getUint16(28, true);

    if (bpp !== 8) {
        throw new Error('Only 8-bit BMP files are supported');
    }

    const doc = new ImageDocument(w, h);

    // Read palette (starts at offset 54 for 40-byte DIB header)
    const paletteOffset = 14 + dibHeaderSize;
    for (let i = 0; i < 256; i++) {
        const off = paletteOffset + i * 4;
        const b = bytes[off];
        const g = bytes[off + 1];
        const r = bytes[off + 2];
        doc.palette.setColor(i, r, g, b);
    }

    // Read pixels
    const rowStride = Math.ceil(w / 4) * 4;
    const layer = doc.getActiveLayer();
    for (let y = 0; y < h; y++) {
        const srcRow = topDown ? y : (h - 1 - y);
        const rowStart = pixelDataOffset + srcRow * rowStride;
        for (let x = 0; x < w; x++) {
            layer.setPixel(x, y, bytes[rowStart + x]);
        }
    }

    return doc;
}

// ─── PCX (8-bit, 256 colors, RLE) ───────────────────────────────────────

export function exportPCX(doc) {
    const flat = doc.flattenToLayer();
    const w = doc.width;
    const h = doc.height;

    // RLE encode pixel data
    const rleData = [];
    const bytesPerLine = w % 2 === 0 ? w : w + 1; // must be even

    for (let y = 0; y < h; y++) {
        let x = 0;
        while (x < bytesPerLine) {
            const rawVal = x < w ? flat.getPixel(x, y) : 0;
            const val = rawVal === TRANSPARENT ? 0 : rawVal;
            let count = 1;
            while (count < 63 && (x + count) < bytesPerLine) {
                const nextRaw = (x + count) < w ? flat.getPixel(x + count, y) : 0;
                const nextVal = nextRaw === TRANSPARENT ? 0 : nextRaw;
                if (nextVal !== val) break;
                count++;
            }

            if (count > 1 || (val & 0xC0) === 0xC0) {
                rleData.push(0xC0 | count);
                rleData.push(val);
            } else {
                rleData.push(val);
            }
            x += count;
        }
    }

    // Total size: 128 header + RLE data + 1 marker + 768 palette
    const totalSize = 128 + rleData.length + 1 + 768;
    const buffer = new ArrayBuffer(totalSize);
    const bytes = new Uint8Array(buffer);
    const view = new DataView(buffer);

    // PCX Header (128 bytes)
    bytes[0] = 0x0A;  // manufacturer
    bytes[1] = 5;     // version 5 (with 256-color palette)
    bytes[2] = 1;     // RLE encoding
    bytes[3] = 8;     // bits per pixel per plane

    // Window: xMin, yMin, xMax, yMax
    view.setUint16(4, 0, true);
    view.setUint16(6, 0, true);
    view.setUint16(8, w - 1, true);
    view.setUint16(10, h - 1, true);

    // DPI
    view.setUint16(12, 72, true);
    view.setUint16(14, 72, true);

    // 16-color palette (48 bytes at offset 16) — unused for 256-color
    // Reserved byte
    bytes[64] = 0;
    // Num planes
    bytes[65] = 1;
    // Bytes per line
    view.setUint16(66, bytesPerLine, true);
    // Palette type (1 = color)
    view.setUint16(68, 1, true);

    // RLE data
    let off = 128;
    for (const b of rleData) {
        bytes[off++] = b;
    }

    // 256-color palette marker
    bytes[off++] = 0x0C;

    // 256-color palette (768 bytes: R, G, B)
    for (let i = 0; i < 256; i++) {
        const [r, g, b] = doc.palette.getColor(i);
        bytes[off++] = r;
        bytes[off++] = g;
        bytes[off++] = b;
    }

    return new Blob([buffer], { type: 'application/octet-stream' });
}

export function importPCX(arrayBuffer) {
    const bytes = new Uint8Array(arrayBuffer);
    const view = new DataView(arrayBuffer);

    if (bytes[0] !== 0x0A) {
        throw new Error('Not a valid PCX file');
    }

    const bpp = bytes[3];
    const numPlanes = bytes[65];
    if (bpp !== 8 || numPlanes !== 1) {
        throw new Error('Only 8-bit single-plane PCX files are supported');
    }

    const xMin = view.getUint16(4, true);
    const yMin = view.getUint16(6, true);
    const xMax = view.getUint16(8, true);
    const yMax = view.getUint16(10, true);
    const w = xMax - xMin + 1;
    const h = yMax - yMin + 1;
    const bytesPerLine = view.getUint16(66, true);

    const doc = new ImageDocument(w, h);

    // Read 256-color palette from end of file
    const palOffset = arrayBuffer.byteLength - 768;
    if (bytes[palOffset - 1] === 0x0C) {
        for (let i = 0; i < 256; i++) {
            const off = palOffset + i * 3;
            doc.palette.setColor(i, bytes[off], bytes[off + 1], bytes[off + 2]);
        }
    }

    // Decode RLE pixel data
    const layer = doc.getActiveLayer();
    let srcOff = 128;
    for (let y = 0; y < h; y++) {
        let x = 0;
        while (x < bytesPerLine) {
            let byte = bytes[srcOff++];
            let count = 1;
            let value = byte;

            if ((byte & 0xC0) === 0xC0) {
                count = byte & 0x3F;
                value = bytes[srcOff++];
            }

            for (let c = 0; c < count; c++) {
                if (x < w) {
                    layer.setPixel(x, y, value);
                }
                x++;
            }
        }
    }

    return doc;
}

// ─── PNG Export ──────────────────────────────────────────────────────────

export function exportPNG(doc, renderer) {
    const imageData = renderer.composite();
    const canvas = document.createElement('canvas');
    canvas.width = doc.width;
    canvas.height = doc.height;
    const ctx = canvas.getContext('2d');
    ctx.putImageData(imageData, 0, 0);

    return new Promise(resolve => {
        canvas.toBlob(blob => resolve(blob), 'image/png');
    });
}

// ─── Per-Layer PNG Export (zipped) ───────────────────────────────────────
// One PNG per layer, each at the layer's own size, named by layer name.
// Layer opacity is baked into the alpha channel; TRANSPARENT pixels stay clear.
// Layers whose (sanitized, case-insensitive) name already appears are skipped.

function sanitizeLayerFilename(name) {
    return sanitizeFilename(name, 'Layer');
}

function layerToImageData(doc, layer) {
    const w = layer.width, h = layer.height;
    const out = new ImageData(w, h);
    const dst = out.data;

    // Text layers are positioned in document space — render the layer alone
    // into a doc-sized buffer (reusing the real renderer) and crop its rect.
    if (layer.type === 'text' && layer.textData) {
        const fakeDoc = {
            width: doc.width, height: doc.height, palette: doc.palette,
            layers: [layer], animationEnabled: false, onionSkinning: false, frames: [],
        };
        const full = new Renderer(fakeDoc).composite();
        const src = full.data;
        for (let y = 0; y < h; y++) {
            const dy = layer.offsetY + y;
            if (dy < 0 || dy >= doc.height) continue;
            for (let x = 0; x < w; x++) {
                const dx = layer.offsetX + x;
                if (dx < 0 || dx >= doc.width) continue;
                const s = (dy * doc.width + dx) * 4;
                const d = (y * w + x) * 4;
                dst[d] = src[s]; dst[d + 1] = src[s + 1];
                dst[d + 2] = src[s + 2]; dst[d + 3] = src[s + 3];
            }
        }
        return out;
    }

    // Pixel layer: map indices straight to RGBA, baking opacity into alpha.
    const opacity = layer.opacity !== undefined ? layer.opacity : 1;
    const alpha = Math.round(opacity * 255);
    const data = layer.data, palette = doc.palette;
    for (let i = 0; i < w * h; i++) {
        const ci = data[i];
        if (ci === TRANSPARENT) continue;
        const [r, g, b] = palette.getColor(ci);
        const off = i * 4;
        dst[off] = r; dst[off + 1] = g; dst[off + 2] = b; dst[off + 3] = alpha;
    }
    return out;
}

function imageDataToPNGBlob(imageData) {
    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    canvas.getContext('2d').putImageData(imageData, 0, 0);
    return new Promise(resolve => canvas.toBlob(blob => resolve(blob), 'image/png'));
}

export async function exportLayersPNGZip(doc) {
    const zip = new JSZip();
    const usedNames = new Set();
    let count = 0;
    for (const layer of doc.layers) {
        const name = sanitizeLayerFilename(layer.name);
        const key = name.toLowerCase();
        if (usedNames.has(key)) continue; // first layer with this name wins
        usedNames.add(key);
        const blob = await imageDataToPNGBlob(layerToImageData(doc, layer));
        zip.file(name + '.png', blob);
        count++;
    }
    if (count === 0) return null;
    return zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
}

// ─── Per-Frame PNG Export (zipped) ───────────────────────────────────────
// One PNG per animation frame, composited at document size, named
// <basename>_<tag>_<n>.png — the underscore form the sprite sheet itself uses,
// so the files feed straight back into texture packing. The _<n> is omitted
// when a tag holds a single frame, so a one-frame `die` is `fox_die.png` rather
// than `fox_die_1.png`.

export async function exportFramesPNGZip(doc, renderer, basename) {
    const names = frameExportNames(doc, basename);
    if (names.length === 0) return null;

    const zip = new JSZip();
    await forEachFrameImage(doc, renderer, async (imageData, i) => {
        zip.file(names[i], await imageDataToPNGBlob(imageData));
    });

    return zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
}

// ─── PAL Format ─────────────────────────────────────────────────────────
// 6-bit: raw 768 bytes (256 × RGB, values 0-63)
// 8-bit: JASC-PAL text format (256 × RGB, values 0-255)

export function exportPAL(palette, is6bit) {
    if (is6bit) {
        const data = new Uint8Array(768);
        for (let i = 0; i < 256; i++) {
            const [r, g, b] = palette.getColor(i);
            data[i * 3]     = Math.round(r / 4);
            data[i * 3 + 1] = Math.round(g / 4);
            data[i * 3 + 2] = Math.round(b / 4);
        }
        return new Blob([data], { type: 'application/octet-stream' });
    }
    let text = 'JASC-PAL\r\n0100\r\n256\r\n';
    for (let i = 0; i < 256; i++) {
        const [r, g, b] = palette.getColor(i);
        text += `${r} ${g} ${b}\r\n`;
    }
    return new Blob([text], { type: 'text/plain' });
}

export function importPAL(bytes) {
    // Raw 768-byte 6-bit PAL
    if (bytes.length === 768) {
        const colors = [];
        for (let i = 0; i < 256; i++) {
            colors.push([bytes[i * 3] * 4, bytes[i * 3 + 1] * 4, bytes[i * 3 + 2] * 4]);
        }
        return colors;
    }
    // JASC-PAL text format
    const text = new TextDecoder().decode(bytes);
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines[0] !== 'JASC-PAL') return null;
    const count = parseInt(lines[2]) || 256;
    const colors = [];
    for (let i = 0; i < count && i + 3 < lines.length; i++) {
        const parts = lines[i + 3].trim().split(/\s+/).map(Number);
        if (parts.length >= 3) {
            colors.push([parts[0], parts[1], parts[2]]);
        }
    }
    while (colors.length < 256) colors.push([0, 0, 0]);
    return colors;
}

// ─── ICO (Windows Icon) ─────────────────────────────────────────────────

const VALID_ICO_SIZES = new Set([16, 24, 32, 48, 64, 128, 256]);

export function getValidICOLayers(doc) {
    const result = [];
    for (let i = 0; i < doc.layers.length; i++) {
        const layer = doc.layers[i];
        if (layer.isFixedSize && layer.width === layer.height && VALID_ICO_SIZES.has(layer.width)) {
            result.push({ index: i, layer, size: layer.width });
        }
    }
    return result;
}

export function exportICO(doc, layerIndices) {
    const count = layerIndices.length;
    const headerSize = 6;
    const dirSize = 16 * count;

    // Build each image entry (BMP DIB + palette + pixels + AND mask)
    const entries = [];
    for (const idx of layerIndices) {
        const layer = doc.layers[idx];
        const w = layer.width;
        const h = layer.height;
        const rowStride = Math.ceil(w / 4) * 4; // pixel rows padded to 4 bytes
        const andRowStride = Math.ceil(w / 32) * 4; // AND mask rows padded to 4 bytes
        const pixelDataSize = rowStride * h;
        const andMaskSize = andRowStride * h;
        const paletteSize = 256 * 4;
        const dibHeaderSize = 40;
        const totalSize = dibHeaderSize + paletteSize + pixelDataSize + andMaskSize;

        const buffer = new ArrayBuffer(totalSize);
        const view = new DataView(buffer);
        const bytes = new Uint8Array(buffer);

        // BITMAPINFOHEADER (40 bytes)
        view.setUint32(0, 40, true);             // header size
        view.setInt32(4, w, true);               // width
        view.setInt32(8, h * 2, true);           // height (doubled for ICO: XOR + AND)
        view.setUint16(12, 1, true);             // color planes
        view.setUint16(14, 8, true);             // bits per pixel
        view.setUint32(16, 0, true);             // compression (none)
        view.setUint32(20, pixelDataSize + andMaskSize, true); // image size
        view.setInt32(24, 0, true);              // h resolution
        view.setInt32(28, 0, true);              // v resolution
        view.setUint32(32, 256, true);           // colors used
        view.setUint32(36, 0, true);             // important colors

        // Color table (256 entries: B, G, R, 0)
        let off = 40;
        for (let i = 0; i < 256; i++) {
            const [r, g, b] = doc.palette.getColor(i);
            bytes[off++] = b;
            bytes[off++] = g;
            bytes[off++] = r;
            bytes[off++] = 0;
        }

        // Pixel data (bottom-to-top, TRANSPARENT → index 0)
        const pixelOffset = dibHeaderSize + paletteSize;
        for (let y = 0; y < h; y++) {
            const srcRow = h - 1 - y; // BMP is bottom-up
            const dstRowStart = pixelOffset + y * rowStride;
            for (let x = 0; x < w; x++) {
                const v = layer.getPixel(x, srcRow);
                bytes[dstRowStart + x] = v === TRANSPARENT ? 0 : v;
            }
        }

        // AND mask (1-bit, bottom-to-top: 1 = transparent, 0 = opaque)
        const andOffset = pixelOffset + pixelDataSize;
        for (let y = 0; y < h; y++) {
            const srcRow = h - 1 - y;
            const dstRowStart = andOffset + y * andRowStride;
            for (let x = 0; x < w; x++) {
                const v = layer.getPixel(x, srcRow);
                if (v === TRANSPARENT) {
                    bytes[dstRowStart + (x >> 3)] |= (0x80 >> (x & 7));
                }
            }
        }

        entries.push({ buffer, w, h });
    }

    // Compute offsets
    const totalFileSize = headerSize + dirSize + entries.reduce((s, e) => s + e.buffer.byteLength, 0);
    const fileBuffer = new ArrayBuffer(totalFileSize);
    const fileView = new DataView(fileBuffer);
    const fileBytes = new Uint8Array(fileBuffer);

    // ICO header
    fileView.setUint16(0, 0, true);       // reserved
    fileView.setUint16(2, 1, true);       // type: 1 = ICO
    fileView.setUint16(4, count, true);   // image count

    // Directory entries + image data
    let dataOffset = headerSize + dirSize;
    for (let i = 0; i < count; i++) {
        const e = entries[i];
        const dirOff = headerSize + i * 16;
        fileBytes[dirOff + 0] = e.w >= 256 ? 0 : e.w;  // width (0 = 256)
        fileBytes[dirOff + 1] = e.h >= 256 ? 0 : e.h;  // height (0 = 256)
        fileBytes[dirOff + 2] = 0;                       // color count (0 = 256+)
        fileBytes[dirOff + 3] = 0;                       // reserved
        fileView.setUint16(dirOff + 4, 1, true);         // color planes
        fileView.setUint16(dirOff + 6, 8, true);         // bits per pixel
        fileView.setUint32(dirOff + 8, e.buffer.byteLength, true);  // image size
        fileView.setUint32(dirOff + 12, dataOffset, true);          // data offset

        fileBytes.set(new Uint8Array(e.buffer), dataOffset);
        dataOffset += e.buffer.byteLength;
    }

    return new Blob([fileBuffer], { type: 'image/x-icon' });
}

// ─── File download helper ───────────────────────────────────────────────

export async function downloadBlob(blob, filename) {
    if (window.electronAPI) {
        const ext = filename.split('.').pop().toLowerCase();
        const filters = [{ name: ext.toUpperCase() + ' files', extensions: [ext] }];
        const filePath = await window.electronAPI.showSaveDialog({
            defaultPath: filename,
            filters,
        });
        if (filePath) {
            const arrayBuffer = await blob.arrayBuffer();
            await window.electronAPI.saveFile(filePath, arrayBuffer);
        }
        return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}
